import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calculator, AlertCircle, Home, Sofa, Utensils, Bed, Briefcase, Wrench, Camera, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const CoverageCalculator = () => {
  const [rooms, setRooms] = useState({
    livingRoom: '',
    kitchen: '',
    bedrooms: '',
    office: '',
    garage: '',
    other: ''
  });

  const [total, setTotal] = useState(0);
  const [showResult, setShowResult] = useState(false);

  const roomConfig = [
    { key: 'livingRoom', label: 'Living Room', icon: Sofa, placeholder: '15000', color: 'from-cyan-500 to-cyan-600' },
    { key: 'kitchen', label: 'Kitchen', icon: Utensils, placeholder: '8000', color: 'from-emerald-500 to-emerald-600' },
    { key: 'bedrooms', label: 'Bedrooms (all)', icon: Bed, placeholder: '20000', color: 'from-cyan-500 to-emerald-500' },
    { key: 'office', label: 'Home Office', icon: Briefcase, placeholder: '5000', color: 'from-teal-500 to-cyan-600' },
    { key: 'garage', label: 'Garage/Storage', icon: Wrench, placeholder: '3000', color: 'from-emerald-500 to-teal-600' },
    { key: 'other', label: 'Other Areas', icon: Home, placeholder: '2000', color: 'from-cyan-600 to-emerald-600' }
  ];

  const handleInputChange = (key, value) => {
    setRooms({
      ...rooms,
      [key]: value
    });
    setShowResult(false);
  };

  const calculateTotal = () => {
    const sum = Object.values(rooms).reduce((acc, val) => {
      const num = parseFloat(val) || 0;
      return acc + num;
    }, 0);
    
    setTotal(sum);
    setShowResult(true);
    
    if (sum > 0) {
      toast.success('Estimate calculated!', {
        description: 'Remember to consult your insurer for accurate coverage advice.'
      });
    } else {
      toast.error('Please enter at least one room value');
    }
  };

  const resetCalculator = () => {
    setRooms({
      livingRoom: '',
      kitchen: '',
      bedrooms: '',
      office: '',
      garage: '',
      other: ''
    });
    setTotal(0);
    setShowResult(false);
  };

  return (
    <section id="tools" data-testid="coverage-calculator" className="py-28 bg-paper">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
          <Badge className="bg-cyan-100 text-cyan-700 hover:bg-cyan-200 px-4 py-1 text-sm">
            Interactive Tool
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900">
            Coverage Calculator
          </h2>
          <p className="text-xl text-slate-600">
            Get a rough estimate of your contents value to understand if you might be under-insured
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <Card className="border-2 border-slate-200 shadow-xl">
            <CardHeader className="bg-gradient-to-br from-slate-50 to-cyan-50 border-b border-slate-200">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-xl flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Estimate Your Contents Value</CardTitle>
                  <CardDescription className="text-base">
                    Enter approximate values for items in each room
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {roomConfig.map((room) => (
                  <div key={room.key} className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm font-semibold text-slate-700">
                      <div className={`w-8 h-8 bg-gradient-to-br ${room.color} rounded-lg flex items-center justify-center`}>
                        <room.icon className="w-4 h-4 text-white" />
                      </div>
                      <span>{room.label}</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 font-medium">
                        $
                      </span>
                      <Input
                        type="number"
                        value={rooms[room.key]}
                        onChange={(e) => handleInputChange(room.key, e.target.value)}
                        placeholder={room.placeholder}
                        data-testid={`calc-input-${room.key}`}
                        className="pl-7 py-6 text-lg border-slate-300 focus:border-cyan-500 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-4 justify-center mb-6">
                <Button
                  onClick={calculateTotal}
                  size="lg"
                  data-testid="calc-submit-button"
                  className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-300"
                >
                  <Calculator className="mr-2 h-5 w-5" />
                  Calculate Total
                </Button>
                <Button
                  onClick={resetCalculator}
                  size="lg"
                  variant="outline"
                  data-testid="calc-reset-button"
                  className="border-2 border-slate-300 px-8 py-6 text-lg font-semibold rounded-xl hover:bg-slate-50"
                >
                  Reset
                </Button>
              </div>

              {/* Result display */}
              {showResult && total > 0 && (
                <div className="space-y-6" data-testid="calc-result">
                  <div className="bg-gradient-to-br from-cyan-50 to-emerald-50 rounded-2xl p-8 border-2 border-cyan-200 space-y-4">
                    <div className="text-center">
                      <p className="text-slate-600 text-lg mb-2">Estimated Total Contents Value</p>
                      <p className="text-5xl font-bold text-slate-900">
                        ${total.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-cyan-200">
                      <div className="flex items-start space-x-3">
                        <AlertCircle className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-slate-700 leading-relaxed">
                          <p className="font-semibold mb-2">This is a rough estimate only.</p>
                          <p>Your actual sum insured should be determined with your insurance broker or insurer, taking into account:</p>
                          <ul className="list-disc ml-5 mt-2 space-y-1">
                            <li>Accurate replacement costs (not second-hand values)</li>
                            <li>Seasonal variations (sports equipment, clothing)</li>
                            <li>High-value items that may need separate coverage</li>
                            <li>Your policy terms and conditions</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* VerifAEye CTA */}
                  <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 rounded-2xl p-8 relative overflow-hidden group cursor-pointer hover:shadow-2xl transition-all duration-300">
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41IiBvcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                          <Camera className="w-8 h-8 text-slate-900" />
                        </div>
                        <div className="text-white">
                          <p className="text-2xl font-bold mb-1">Now let's see how good your guesswork was</p>
                          <p className="text-slate-300 text-lg">Click here to use VerifAEye to calculate the true value of your contents</p>
                        </div>
                      </div>
                      <a 
                        href="https://verifaeye.com.au" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex-shrink-0"
                      >
                        <Button
                          size="lg"
                          className="bg-gradient-to-r from-cyan-400 to-emerald-400 hover:from-cyan-500 hover:to-emerald-500 text-slate-900 px-8 py-6 text-lg font-bold rounded-xl shadow-lg transform group-hover:scale-105 transition-all duration-300"
                        >
                          Get VerifAEye
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Disclaimer */}
              <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-900">
                  <span className="font-semibold">Important:</span> This calculator provides general guidance only. 
                  Always consult with your insurance broker or insurer for specific advice tailored to your circumstances.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default CoverageCalculator;
